import { apiClient } from './apiClient';

export type ObjectStorageOverview = {
  isConfigured: boolean;
  isCompanyConfigured: boolean;
  provider: string;
  endpoint: string;
  bucketName: string;
  useSsl: boolean;
  hasAccessKey: boolean;
  hasSecretKey: boolean;
  accessKeyMasked: string;
  secretKeyMasked: string;
  configurationHint?: string | null;
};

export type UpdateObjectStorageConnectionPayload = {
  provider: string;
  endpoint: string;
  bucketName: string;
  useSsl: boolean;
  accessKey?: string | null;
  secretKey?: string | null;
};

export type ObjectStorageCredentials = {
  accessKey: string;
  secretKey: string;
};

export type ObjectStorageConnectionTestResult = {
  succeeded: boolean;
  status: string;
  checkedAtUtc: string;
  endpoint: string;
  bucketName: string;
  bucketExists?: boolean | null;
  latencyMs?: number | null;
  message: string;
};

export type StorageBucket = {
  name: string;
  creationDateUtc?: string | null;
  isDefault: boolean;
};

export type StorageObjectItem = {
  key: string;
  name: string;
  isFolder: boolean;
  sizeBytes?: number | null;
  lastModifiedUtc?: string | null;
  eTag?: string | null;
};

export type StorageObjectListResult = {
  bucketName: string;
  prefix: string;
  companyPrefix: string;
  items: StorageObjectItem[];
};

export type StorageObjectUploadResult = {
  key: string;
  eTag?: string | null;
};

export type StoragePresignedUrl = {
  key: string;
  url: string;
  expiresAtUtc: string;
};

export type StorageAccessKey = {
  id: string;
  name: string;
  description: string;
  accessKey: string;
  accessKeyMasked: string;
  expiresAtUtc?: string | null;
  revokedAtUtc?: string | null;
  lastUsedAtUtc?: string | null;
  createdAtUtc?: string | null;
  isActive: boolean;
};

export type CreateStorageAccessKeyResult = {
  id: string;
  name: string;
  accessKey: string;
  secretKey: string;
  expiresAtUtc?: string | null;
  createdAtUtc: string;
};

export type StorageAccessKeyCredentials = {
  accessKey: string;
  secretKey: string;
};

export type CreateStorageBucketPayload = {
  name: string;
};

export type CreateStorageFolderPayload = {
  bucketName?: string | null;
  prefix: string;
  folderName: string;
};

export type DeleteStorageObjectsPayload = {
  bucketName?: string | null;
  keys: string[];
};

export type CopyStorageObjectPayload = {
  bucketName?: string | null;
  sourceKey: string;
  destinationKey: string;
  deleteSource?: boolean;
};

export type CreateStorageAccessKeyPayload = {
  name: string;
  description?: string | null;
  expiresAtUtc?: string | null;
};

export type UpdateStorageAccessKeyPayload = {
  name: string;
  description?: string | null;
  expiresAtUtc?: string | null;
};

export async function fetchStorageOverview(): Promise<ObjectStorageOverview> {
  const response = await apiClient.get<ObjectStorageOverview>('/v1/storage/overview');
  return response.data;
}

export async function updateStorageConnection(
  payload: UpdateObjectStorageConnectionPayload,
): Promise<ObjectStorageOverview> {
  const response = await apiClient.put<ObjectStorageOverview>('/v1/storage/connection', payload);
  return response.data;
}

export async function fetchStorageCredentials(): Promise<ObjectStorageCredentials> {
  const response = await apiClient.get<ObjectStorageCredentials>('/v1/storage/credentials');
  return response.data;
}

export async function testStorageConnection(): Promise<ObjectStorageConnectionTestResult> {
  const response = await apiClient.post<ObjectStorageConnectionTestResult>('/v1/storage/test');
  return response.data;
}

export async function listStorageBuckets(): Promise<StorageBucket[]> {
  const response = await apiClient.get<StorageBucket[]>('/v1/storage/buckets');
  return response.data;
}

export async function createStorageBucket(payload: CreateStorageBucketPayload): Promise<StorageBucket> {
  const response = await apiClient.post<StorageBucket>('/v1/storage/buckets', payload);
  return response.data;
}

export async function ensureDefaultStorageBucket(): Promise<void> {
  await apiClient.post('/v1/storage/buckets/ensure-default');
}

export async function deleteStorageBucket(bucketName: string, force = false): Promise<void> {
  await apiClient.delete(`/v1/storage/buckets/${encodeURIComponent(bucketName)}`, {
    params: { force },
  });
}

export async function listStorageObjects(params?: {
  bucket?: string;
  prefix?: string;
  recursive?: boolean;
}): Promise<StorageObjectListResult> {
  const response = await apiClient.get<StorageObjectListResult>('/v1/storage/objects', {
    params,
  });
  return response.data;
}

export async function uploadStorageObject(params: {
  file: File;
  bucket?: string;
  prefix?: string;
  objectName?: string;
}): Promise<StorageObjectUploadResult> {
  const formData = new FormData();
  formData.append('file', params.file);
  if (params.bucket) formData.append('bucket', params.bucket);
  if (params.prefix) formData.append('prefix', params.prefix);
  if (params.objectName) formData.append('objectName', params.objectName);

  const response = await apiClient.post<StorageObjectUploadResult>(
    '/v1/storage/objects/upload',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function createStorageFolder(
  payload: CreateStorageFolderPayload,
): Promise<StorageObjectUploadResult> {
  const response = await apiClient.post<StorageObjectUploadResult>(
    '/v1/storage/objects/folders',
    payload,
  );
  return response.data;
}

export async function deleteStorageObjects(payload: DeleteStorageObjectsPayload): Promise<void> {
  await apiClient.post('/v1/storage/objects/delete', payload);
}

export async function copyStorageObject(
  payload: CopyStorageObjectPayload,
): Promise<StorageObjectUploadResult> {
  const response = await apiClient.post<StorageObjectUploadResult>(
    '/v1/storage/objects/copy',
    payload,
  );
  return response.data;
}

export async function fetchStorageDownloadUrl(params: {
  key: string;
  bucket?: string;
}): Promise<StoragePresignedUrl> {
  const response = await apiClient.get<StoragePresignedUrl>('/v1/storage/objects/download-url', {
    params,
  });
  return response.data;
}

export async function listStorageAccessKeys(): Promise<StorageAccessKey[]> {
  const response = await apiClient.get<StorageAccessKey[]>('/v1/storage/access-keys');
  return response.data;
}

export async function createStorageAccessKey(
  payload: CreateStorageAccessKeyPayload,
): Promise<CreateStorageAccessKeyResult> {
  const response = await apiClient.post<CreateStorageAccessKeyResult>(
    '/v1/storage/access-keys',
    payload,
  );
  return response.data;
}

export async function updateStorageAccessKey(
  id: string,
  payload: UpdateStorageAccessKeyPayload,
): Promise<StorageAccessKey> {
  const response = await apiClient.put<StorageAccessKey>(
    `/v1/storage/access-keys/${encodeURIComponent(id)}`,
    payload,
  );
  return response.data;
}

export async function revokeStorageAccessKey(id: string): Promise<void> {
  await apiClient.post(`/v1/storage/access-keys/${encodeURIComponent(id)}/revoke`);
}

export async function deleteStorageAccessKey(id: string): Promise<void> {
  await apiClient.delete(`/v1/storage/access-keys/${encodeURIComponent(id)}`);
}

export async function revealStorageAccessKeyCredentials(
  id: string,
): Promise<StorageAccessKeyCredentials> {
  const response = await apiClient.get<StorageAccessKeyCredentials>(
    `/v1/storage/access-keys/${encodeURIComponent(id)}/credentials`,
  );
  return response.data;
}
